import { useEffect, useState, useMemo } from "react";
import useLocalStorage from "../../Hooks/useLocalStorage";
import { BASE_URL } from "../../Config/api.config";

// Types based on your API response
interface SystemBreakdown {
  exists: boolean;
  total_transactions: string;
  total_credits: number | string;
  total_debits: number | string;
  recent_30_days: number | string;
}

interface ConsistencyCheck {
  consistent: boolean;
  calculated_balance: number;
  wallet_balance: string;
  difference: number;
}

interface Balance {
  wallet_balance: number;
  purchases_total: number;
  flexicoins_balance: number;
  effective_balance: number;
  consistency_check: ConsistencyCheck;
}

interface Transaction {
  id: string;
  system: string;
  type: string;
  amount: number;
  description: string;
  timestamp: string;
  category: string;
  icon: string;
}

interface UserTransaction {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  flexicoins_balance: string;
  total_flexicoins_purchased: string;
  last_purchase_date: string;
  is_admin: string;
  date_created: string;
  balance: Balance;
  recent_transactions: Transaction[];
  recent_transactions_count: number;
}

interface Summary {
  total_clients: string;
  active_clients: string;
  total_purchases: string;
  total_spent: number;
  total_flexicoins_balance: string;
  total_flexicoins_purchased: string;
  system_breakdown: {
    revenuecat: SystemBreakdown;
    wallet: SystemBreakdown;
    flexicoins: SystemBreakdown;
  };
  consistency_check: {
    clients_vs_purchases_difference: number;
    balance_vs_net_difference: number;
  };
}

interface Pagination {
  limit: number;
  offset: number;
  total_users: number;
  current_page_users: number;
  has_more: boolean;
  total_pages: number;
  current_page: number;
}

interface ApiResponse {
  status: string;
  message: string;
  data: {
    summary: Summary;
    users_transactions: UserTransaction[];
    filters: any;
    pagination: Pagination;
  };
  debug: any;
}

const PaymentTransactions = () => {
  const [adminData] = useLocalStorage("adminData", "");
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "ascending" | "descending";
  }>({
    key: "user_id",
    direction: "descending",
  });
  const [selectedUser, setSelectedUser] = useState<UserTransaction | null>(
    null
  );
  const [activeTab, setActiveTab] = useState<"overview" | "users">("overview");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const fetchData = async (
    page: number = currentPage,
    limit: number = itemsPerPage
  ) => {
    try {
      setLoading(true);
      setError(null);
      const offset = (page - 1) * limit;

      const response = await fetch(
        `${BASE_URL}adm.unified.transactions.php?limit=${limit}&offset=${offset}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${adminData.uid}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse = await response.json();

      if (result.status === "success") {
        setData(result);
        setCurrentPage(result.data.pagination.current_page);
      } else {
        throw new Error(result.message || "Failed to fetch data");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminData?.uid) {
      fetchData();
    }
  }, [adminData]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchData(newPage, itemsPerPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchData(1, newItemsPerPage);
  };

  // Filter and sort users (client-side for current page only)
  const filteredAndSortedUsers = useMemo(() => {
    if (!data?.data.users_transactions) return [];

    let filtered = data.data.users_transactions.filter(
      (user) =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_id.includes(searchTerm)
    );

    // Sort users
    filtered.sort((a, b) => {
      let aValue: any = a[sortConfig.key as keyof UserTransaction];
      let bValue: any = b[sortConfig.key as keyof UserTransaction];

      // Handle numeric sorting for balance fields
      if (
        sortConfig.key.includes("balance") ||
        sortConfig.key.includes("purchased")
      ) {
        aValue = parseFloat(aValue as string) || 0;
        bValue = parseFloat(bValue as string) || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [data, searchTerm, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  const formatNumber = (num: number | string) => {
    const value = typeof num === "string" ? parseFloat(num) : num;
    return new Intl.NumberFormat().format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "0000-00-00 00:00:00") return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getConsistencyStatus = (difference: number) => {
    if (Math.abs(difference) <= 1)
      return { status: "success", text: "Consistent" };
    if (Math.abs(difference) <= 10)
      return { status: "warning", text: "Minor Discrepancy" };
    return { status: "error", text: "Inconsistent" };
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    if (!data?.data.pagination) return [];

    const { current_page, total_pages } = data.data.pagination;
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, current_page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(total_pages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading transaction data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Error Loading Data
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => fetchData()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No data available</p>
        </div>
      </div>
    );
  }

  const { summary, users_transactions, pagination } = data.data;
  const pageNumbers = getPageNumbers();

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Flexicoins Transactions Dashboard
          </h1>
          <p className="text-gray-600">
            Comprehensive overview of all user transactions and balances
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("overview")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "overview"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                System Overview
              </button>
              <button
                onClick={() => setActiveTab("users")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "users"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Users ({pagination.total_users})
              </button>
            </nav>
          </div>
        </div>

        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total Clients
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(summary.total_clients)}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <span className="text-blue-600 text-xl">👥</span>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-green-600 font-medium">
                    {summary.active_clients} active
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total Flexicoins
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(summary.total_flexicoins_balance)}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    <span className="text-green-600 text-xl">💰</span>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-600">
                    {formatNumber(summary.total_flexicoins_purchased)} purchased
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      RevenueCat Purchases
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(summary.total_purchases)}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <span className="text-purple-600 text-xl">🛒</span>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-600">
                    {summary.system_breakdown.revenuecat.total_transactions}{" "}
                    transactions
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total Spent
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(summary.total_spent)}
                    </p>
                  </div>
                  <div className="p-3 bg-red-100 rounded-lg">
                    <span className="text-red-600 text-xl">📤</span>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-600">
                    Across all systems
                  </span>
                </div>
              </div>
            </div>

            {/* System Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                System Breakdown
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {Object.entries(summary.system_breakdown).map(
                  ([system, data]) => (
                    <div
                      key={system}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 capitalize">
                          {system}
                        </h4>
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            data.exists
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {data.exists ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Transactions:</span>
                          <span className="font-medium">
                            {formatNumber(data.total_transactions)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Credits:</span>
                          <span className="font-medium text-green-600">
                            {formatNumber(data.total_credits)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Debits:</span>
                          <span className="font-medium text-red-600">
                            {formatNumber(data.total_debits)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Last 30d:</span>
                          <span className="font-medium">
                            {formatNumber(data.recent_30_days)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Consistency Check */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Data Consistency
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(summary.consistency_check).map(
                  ([key, difference]) => {
                    const consistency = getConsistencyStatus(difference);
                    return (
                      <div
                        key={key}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900 capitalize">
                            {key.replace(/_/g, " ")}
                          </h4>
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              consistency.status === "success"
                                ? "bg-green-100 text-green-800"
                                : consistency.status === "warning"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {consistency.text}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Difference: {formatNumber(difference)}
                        </p>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "users" && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                <div className="flex-1 w-full sm:max-w-md">
                  <input
                    type="text"
                    placeholder="Search users by email, name, or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center gap-4">
                  {/* Items per page selector */}
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>

                  <div className="text-sm text-gray-600">
                    Showing {filteredAndSortedUsers.length} of{" "}
                    {pagination.current_page_users} users on this page
                    <br />
                    <span className="text-xs">
                      Total: {pagination.total_users} users
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        { key: "user_id", label: "User ID" },
                        { key: "full_name", label: "Name" },
                        { key: "email", label: "Email" },
                        { key: "flexicoins_balance", label: "Balance" },
                        {
                          key: "total_flexicoins_purchased",
                          label: "Total Purchased",
                        },
                        { key: "last_purchase_date", label: "Last Purchase" },
                        { key: "date_created", label: "Joined" },
                        { key: "is_admin", label: "Admin" },
                      ].map(({ key, label }) => (
                        <th
                          key={key}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort(key)}
                        >
                          <div className="flex items-center space-x-1">
                            <span>{label}</span>
                            {sortConfig.key === key && (
                              <span>
                                {sortConfig.direction === "ascending"
                                  ? "↑"
                                  : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAndSortedUsers.map((user) => (
                      <tr
                        key={user.user_id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedUser(user)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{user.user_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                            <div className="font-medium">{user.full_name}</div>
                            {user.first_name && user.last_name && (
                              <div className="text-gray-500 text-xs">
                                {user.first_name} {user.last_name}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`font-medium ${
                              parseFloat(user.flexicoins_balance) > 0
                                ? "text-green-600"
                                : "text-gray-600"
                            }`}
                          >
                            {formatNumber(user.flexicoins_balance)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(user.total_flexicoins_purchased)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(user.last_purchase_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(user.date_created)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.is_admin === "1"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {user.is_admin === "1" ? "Admin" : "User"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUser(user);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {pagination && pagination.total_pages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.has_more}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{" "}
                        <span className="font-medium">
                          {(currentPage - 1) * itemsPerPage + 1}
                        </span>{" "}
                        to{" "}
                        <span className="font-medium">
                          {Math.min(
                            currentPage * itemsPerPage,
                            pagination.total_users
                          )}
                        </span>{" "}
                        of{" "}
                        <span className="font-medium">
                          {pagination.total_users}
                        </span>{" "}
                        results
                      </p>
                    </div>
                    <div>
                      <nav
                        className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                        aria-label="Pagination"
                      >
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>←
                        </button>

                        {/* Page numbers */}
                        {pageNumbers.map((page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === page
                                ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </button>
                        ))}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={!pagination.has_more}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>→
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Empty State */}
            {filteredAndSortedUsers.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🔍</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No users found
                </h3>
                <p className="text-gray-600">
                  {searchTerm
                    ? "Try adjusting your search criteria"
                    : "No users available"}
                </p>
              </div>
            )}
          </div>
        )}

        {/* User Detail Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedUser.full_name}
                    </h2>
                    <p className="text-gray-600">{selectedUser.email}</p>
                  </div>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* User Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      User Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">User ID:</span>
                        <span className="font-medium">
                          #{selectedUser.user_id}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Admin:</span>
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            selectedUser.is_admin === "1"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {selectedUser.is_admin === "1" ? "Yes" : "No"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Joined:</span>
                        <span className="font-medium">
                          {formatDate(selectedUser.date_created)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Purchase:</span>
                        <span className="font-medium">
                          {formatDate(selectedUser.last_purchase_date)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Balance Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Balance Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current Balance:</span>
                        <span className="font-medium text-green-600">
                          {formatNumber(selectedUser.flexicoins_balance)}{" "}
                          flexicoins
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Purchased:</span>
                        <span className="font-medium">
                          {formatNumber(
                            selectedUser.total_flexicoins_purchased
                          )}{" "}
                          flexicoins
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Wallet Balance:</span>
                        <span className="font-medium">
                          {formatNumber(selectedUser.balance.wallet_balance)}{" "}
                          credits
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Effective Balance:
                        </span>
                        <span className="font-medium">
                          {formatNumber(selectedUser.balance.effective_balance)}{" "}
                          flexicoins
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Transactions */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Recent Transactions (
                    {selectedUser.recent_transactions_count})
                  </h3>
                  {selectedUser.recent_transactions.length > 0 ? (
                    <div className="space-y-2">
                      {selectedUser.recent_transactions.map((transaction) => (
                        <div
                          key={transaction.id}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-xl">{transaction.icon}</span>
                            <div>
                              <div className="font-medium">
                                {transaction.description}
                              </div>
                              <div className="text-sm text-gray-500 capitalize">
                                {transaction.system} • {transaction.type}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div
                              className={`font-medium ${
                                transaction.amount >= 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {transaction.amount >= 0 ? "+" : ""}
                              {formatNumber(transaction.amount)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDate(transaction.timestamp)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No recent transactions found
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentTransactions;
